import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { CheckCircle, XCircle, AlertTriangle, ExternalLink, Eye, RefreshCw, DollarSign, Loader2, Image, Clock, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

const OVERRIDE_REASONS = ['Screenshot unclear', 'Post was edited', 'Platform reporting lag', 'Other'];

// ==================== VERIFICATION TAB ====================
export function VerificationTab() {
  const [view, setView] = useState('pending');
  const [pending, setPending] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlagged, setSelectedFlagged] = useState(null);
  const [overrideNum, setOverrideNum] = useState('');
  const [overrideReason, setOverrideReason] = useState(OVERRIDE_REASONS[0]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, f, a] = await Promise.all([
        axios.get(`${API}/submissions/pending`, { headers: getAuthHeaders() }),
        axios.get(`${API}/submissions/flagged`, { headers: getAuthHeaders() }),
        axios.get(`${API}/submissions/approved`, { headers: getAuthHeaders() }),
      ]);
      setPending(p.data); setFlagged(f.data); setApproved(a.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); const interval = setInterval(fetchAll, 60000); return () => clearInterval(interval); }, []);

  const handleRetry = async (id) => {
    try {
      await axios.post(`${API}/submissions/${id}/verify`, {}, { headers: getAuthHeaders() });
      fetchAll();
    } catch {}
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/submissions/${id}/approve`, {}, { headers: getAuthHeaders() });
      setSelectedFlagged(null);
      fetchAll();
    } catch {}
  };

  const handleOverride = async (id) => {
    if (!overrideNum) return;
    try {
      await axios.post(`${API}/submissions/${id}/override`, {
        impressions: parseInt(overrideNum), reason: overrideReason
      }, { headers: getAuthHeaders() });
      setSelectedFlagged(null); setOverrideNum('');
      fetchAll();
    } catch {}
  };

  const handlePayout = async (id) => {
    try {
      await axios.post(`${API}/submissions/${id}/payout`, {}, { headers: getAuthHeaders() });
      fetchAll();
    } catch {}
  };

  const handlePayoutAll = async () => {
    try {
      const { data } = await axios.post(`${API}/submissions/payout-all`, {}, { headers: getAuthHeaders() });
      alert(`${data.processed} payouts processed`);
      fetchAll();
    } catch {}
  };

  return (
    <div data-testid="verification-tab">
      {/* Sub-nav */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'pending', label: 'Pending', count: pending.length, color: 'text-[#9CA3AF]' },
          { key: 'flagged', label: 'Flagged', count: flagged.length, color: 'text-yellow-400' },
          { key: 'approved', label: 'Awaiting Payout', count: approved.length, color: 'text-[#10B981]' },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`view-${v.key}`}
            className={`text-xs px-4 py-2 border transition-colors ${view === v.key ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>
            {v.label} <span className={`ml-1 font-mono ${v.color}`}>({v.count})</span>
          </button>
        ))}
        <button onClick={fetchAll} className="ml-auto text-[#6B7280] hover:text-white p-1"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#FF4343] animate-spin" /></div>}

      {/* PENDING */}
      {!loading && view === 'pending' && (
        pending.length === 0 ? <p className="text-[#6B7280] text-sm">No pending verifications.</p> : (
          <div className="space-y-2">
            {pending.map(s => (
              <div key={s.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#9CA3AF]" />
                  <div>
                    <span className="text-xs text-white">{s.executive_name}</span>
                    <span className="text-[10px] text-[#6B7280] ml-2">{s.campaign_title}</span>
                  </div>
                  <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5 capitalize">{s.post_platform}</span>
                </div>
                <button onClick={() => handleRetry(s.id)} data-testid={`retry-${s.id}`}
                  className="text-[10px] px-3 py-1 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-white transition-colors">
                  <RefreshCw className="w-3 h-3 inline mr-1" />Retry
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* FLAGGED */}
      {!loading && view === 'flagged' && (
        flagged.length === 0 ? <p className="text-[#6B7280] text-sm">No flagged submissions.</p> : (
          <div className="space-y-3">
            {flagged.map(s => (
              <div key={s.id} className="bg-[#111827] border border-white/10 border-l-[3px] border-l-yellow-400 p-4" data-testid={`flagged-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm text-white font-medium">{s.executive_name}</span>
                    <span className="text-xs text-[#6B7280] ml-2">{s.campaign_title} · {s.brand_name}</span>
                  </div>
                  <span className="text-[10px] font-mono text-yellow-400 bg-yellow-400/10 px-2 py-0.5 border border-yellow-400/20">NEEDS REVIEW</span>
                </div>
                {/* Flag reason */}
                <div className="bg-yellow-400/5 border border-yellow-400/10 p-3 mb-3">
                  <p className="text-xs text-yellow-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {s.anomaly_reason}</p>
                </div>
                {/* Metrics */}
                <div className="flex gap-4 text-xs text-[#6B7280] mb-3">
                  <span>Platform: <span className="text-white capitalize">{s.post_platform}</span></span>
                  <span>Claimed: <span className="font-mono text-white">{formatNum(s.impressions)}</span></span>
                  <span>Hist. avg: <span className="font-mono text-white">{formatNum(s.executive_historical_avg)}</span></span>
                  <span>Confidence: <span className={`font-mono ${s.extraction_confidence === 'high' ? 'text-[#10B981]' : s.extraction_confidence === 'medium' ? 'text-yellow-400' : 'text-[#FF4343]'}`}>{s.extraction_confidence || '—'}</span></span>
                </div>
                {/* Screenshot + URL */}
                <div className="flex gap-3 mb-3">
                  {s.post_url && <a href={s.post_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#9CA3AF] hover:text-white flex items-center gap-1">View Post <ExternalLink className="w-3 h-3" /></a>}
                  {s.screenshot_url && <a href={s.screenshot_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#9CA3AF] hover:text-white flex items-center gap-1"><Image className="w-3 h-3" /> View Screenshot</a>}
                </div>
                {/* Actions */}
                <div className="flex gap-2">
                  <button onClick={() => handleApprove(s.id)} data-testid={`approve-flagged-${s.id}`}
                    className="text-[10px] px-3 py-1.5 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors">Approve as Submitted</button>
                  <button onClick={() => { setSelectedFlagged(s); setOverrideNum(''); }} data-testid={`override-btn-${s.id}`}
                    className="text-[10px] px-3 py-1.5 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">Override Impressions</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* APPROVED — AWAITING PAYOUT */}
      {!loading && view === 'approved' && (
        approved.length === 0 ? <p className="text-[#6B7280] text-sm">No payouts pending.</p> : (
          <>
            {approved.length > 1 && (
              <button onClick={handlePayoutAll} data-testid="payout-all-btn"
                className="mb-4 text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                Process All Pending Payouts ({approved.length})
              </button>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payout-table">
                <thead><tr className="border-b border-white/10">
                  {['Executive','Campaign','Platform','Impressions','Payout','Status','Action'].map(h => (
                    <th key={h} className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {approved.map(s => (
                    <tr key={s.id} className="border-b border-white/5">
                      <td className="py-2 text-white text-xs">{s.executive_name}</td>
                      <td className="py-2 text-[#9CA3AF] text-xs">{s.campaign_title}</td>
                      <td className="py-2 text-xs capitalize text-[#6B7280]">{s.post_platform}</td>
                      <td className="py-2 font-mono text-xs text-white">{formatNum(s.impressions)}</td>
                      <td className="py-2 font-mono text-xs text-[#FF4343]">${(s.payout_amount || 0).toLocaleString()}</td>
                      <td className="py-2"><span className="text-[10px] font-mono uppercase text-[#10B981]">{s.payout_status}</span></td>
                      <td className="py-2">
                        <button onClick={() => handlePayout(s.id)} data-testid={`payout-${s.id}`}
                          className="text-[10px] px-3 py-1 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors">
                          <DollarSign className="w-3 h-3 inline mr-0.5" />Process
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}

      {/* Override Modal */}
      {selectedFlagged && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedFlagged(null)} />
          <div className="relative glass max-w-md w-full p-6" data-testid="override-modal">
            <h3 className="text-sm font-semibold mb-4">Override Impression Count</h3>
            <p className="text-xs text-[#6B7280] mb-3">{selectedFlagged.executive_name} — {selectedFlagged.campaign_title}</p>
            <p className="text-xs text-[#6B7280] mb-3">Current claimed: <span className="font-mono text-white">{formatNum(selectedFlagged.impressions)}</span></p>
            <input type="number" placeholder="Correct impression count" value={overrideNum} onChange={e => setOverrideNum(e.target.value)} data-testid="override-impressions-input"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm mb-3 focus:outline-none focus:border-[#FF4343]" />
            <select value={overrideReason} onChange={e => setOverrideReason(e.target.value)} data-testid="override-reason-select"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm mb-4 focus:outline-none focus:border-[#FF4343]">
              {OVERRIDE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={() => handleOverride(selectedFlagged.id)} data-testid="confirm-override-btn"
                className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
                Override & Approve
              </button>
              <button onClick={() => setSelectedFlagged(null)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== AI COSTS TAB ====================
export function AICostsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/ai-costs`, { headers: getAuthHeaders() }).then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-[#9CA3AF]">Loading...</p>;

  return (
    <div data-testid="ai-costs-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">AI Usage — {data.total_calls} total calls</h3>
      </div>
      {Object.keys(data.summary).length === 0 ? <p className="text-[#6B7280] text-sm">No AI calls logged yet.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {Object.entries(data.summary).map(([task, info]) => (
            <div key={task} className="bg-[#111827] border border-white/10 p-4">
              <h4 className="text-xs font-mono uppercase text-[#FF4343] mb-2">{task.replace(/_/g, ' ')}</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><span className="font-mono text-lg text-white">{info.count}</span><p className="text-[9px] text-[#6B7280]">Calls</p></div>
                <div><span className="font-mono text-lg text-[#10B981]">{info.success}</span><p className="text-[9px] text-[#6B7280]">Success</p></div>
                <div><span className="font-mono text-lg text-[#6B7280]">{info.avg_latency}ms</span><p className="text-[9px] text-[#6B7280]">Avg Latency</p></div>
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {Object.entries(info.models).map(([m, c]) => (
                  <span key={m} className="text-[9px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5">{m}: {c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Recent calls */}
      {data.recent?.length > 0 && (
        <div>
          <h4 className="text-xs font-mono uppercase text-[#6B7280] mb-2">Recent Calls</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-white/10">
                {['Task','Model','Latency','Success','Time'].map(h => <th key={h} className="text-left py-1.5 text-[#6B7280] font-mono text-[9px] uppercase">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.recent.map((l, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-1.5 text-[#9CA3AF]">{l.task_type}</td>
                    <td className="py-1.5 font-mono text-[#6B7280]">{l.model_used}</td>
                    <td className="py-1.5 font-mono text-[#6B7280]">{l.latency_ms}ms</td>
                    <td className="py-1.5">{l.success ? <CheckCircle className="w-3 h-3 text-[#10B981]" /> : <XCircle className="w-3 h-3 text-[#FF4343]" />}</td>
                    <td className="py-1.5 text-[#6B7280]">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== SLA DASHBOARD ====================
export function SLADashboard() {
  const [sla, setSla] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/sla-metrics`, { headers: getAuthHeaders() }).then(r => setSla(r.data)).catch(() => {});
  }, []);
  if (!sla) return <p className="text-[#9CA3AF]">Loading SLA metrics...</p>;

  const metrics = [
    { label: 'Avg Submission → Verification', value: sla.avg_submission_to_verification_formatted, color: 'text-[#10B981]' },
    { label: 'Avg Submission → Approval', value: sla.avg_submission_to_approval_formatted, color: 'text-[#FF4343]' },
    { label: 'Avg Submission → Payout', value: sla.avg_submission_to_payout_formatted, color: 'text-[#FF4343]' },
  ];

  return (
    <div data-testid="sla-dashboard" className="space-y-6">
      <h3 className="text-sm font-semibold">Verification SLA</h3>

      {/* Timing metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {metrics.map(m => (
          <div key={m.label} className="bg-[#111827] border border-white/10 p-5 text-center">
            <span className={`font-mono text-3xl font-bold ${m.color}`}>{m.value}</span>
            <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-2">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Throughput */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Submitted', value: sla.total_submitted, color: 'text-white' },
          { label: 'Verified', value: sla.total_verified, color: 'text-[#10B981]' },
          { label: 'Approved', value: sla.total_approved, color: 'text-[#10B981]' },
          { label: 'Paid', value: sla.total_paid, color: 'text-[#FF4343]' },
          { label: 'Flagged', value: sla.total_flagged, color: 'text-yellow-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#111827] border border-white/10 p-4 text-center">
            <span className={`font-mono text-2xl font-bold ${s.color}`}>{s.value}</span>
            <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#111827] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#9CA3AF]">Auto-Approval Rate</span>
            <span className="font-mono text-2xl font-bold text-[#10B981]">{sla.auto_approval_rate}%</span>
          </div>
          <div className="w-full h-2 bg-white/5"><div className="h-full bg-[#10B981]" style={{ width: `${sla.auto_approval_rate}%` }} /></div>
          <p className="text-[9px] text-[#6B7280] mt-2">Posts verified and approved without manual review</p>
        </div>
        <div className="bg-[#111827] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#9CA3AF]">Flag Rate</span>
            <span className="font-mono text-2xl font-bold text-yellow-400">{sla.flag_rate}%</span>
          </div>
          <div className="w-full h-2 bg-white/5"><div className="h-full bg-yellow-400" style={{ width: `${sla.flag_rate}%` }} /></div>
          <p className="text-[9px] text-[#6B7280] mt-2">Posts requiring manual admin review</p>
        </div>
      </div>
    </div>
  );
}
